import { useQuery } from "@tanstack/react-query";

import { SalesCompositionResponse } from "@/@types/growthos-api/schema/openapi";
import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

export type SalesPeriod = "week" | "month";

export type SalesChannelDataDTO = {
  [key: string]: number[];
};

export type SalesBucketDTO = {
  [key: string]: SalesChannelDataDTO;
};

export type SalesCompositionDTO = {
  x_axis: {
    date: string[];
  };
  y_axis: SalesBucketDTO;
  y_axis_unit: SalesBucketDTO;
};

export type SalesCompositionDTOV2 = {
  x_axis: {
    date: string[];
  };
  y_axis: SalesBucketDTO;
  y_axis_unit: SalesBucketDTO;
  channels: string[];
};

// New types for V2 endpoint that match the updated backend response
export type GroupedSalesCompositionRow = {
  id: string;
  period_start: string;
  geography?: string;
  brand?: string;
  category?: string;
  product?: string;
  sales: Record<string, Record<string, number>>;
  units: Record<string, Record<string, number>>;
};

export type SalesCompositionV2DTO = {
  chartView: SalesCompositionDTOV2;
  tableView: GroupedSalesCompositionRow[];
};

export type GetSalesCompositionV2Request = {
  startDate: string | null;
  endDate: string | null;
  brand: string;
  category: string;
  product: string;
  salesPeriod: SalesPeriod;
  geographies?: string[];
  geoViewToggle?: "aggregate" | "split";
  businessUnitToggle?: "aggregate" | "split";
};

const emptyV2Response: SalesCompositionV2DTO = {
  chartView: {
    x_axis: {
      date: [],
    },
    y_axis: {},
    y_axis_unit: {},
    channels: [],
  },
  tableView: [],
};

const getSalesCompositionNest = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  salesPeriod,
  geographies,
  geoViewToggle = "aggregate",
  businessUnitToggle = "aggregate",
}: {
  startDate: string;
  endDate: string;
  brand: string;
  category: string;
  product: string;
  salesPeriod: SalesPeriod;
  geographies?: string[];
  geoViewToggle?: "aggregate" | "split";
  businessUnitToggle?: "aggregate" | "split";
}): Promise<SalesCompositionResponse> => {
  const params = new URLSearchParams();
  params.append("startDate", startDate);
  params.append("endDate", endDate);
  params.append("brand", brand);
  params.append("category", category);
  params.append("product", product);
  params.append("salesPeriod", salesPeriod);
  if (geographies && Array.isArray(geographies)) {
    geographies.forEach((geo) => {
      params.append("geographies", geo);
    });
  }
  params.append("geoViewToggle", geoViewToggle);
  params.append("businessUnitToggle", businessUnitToggle);

  return await axiosApi.get(`/api/sales-drivers/sales-composition?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

const getSalesCompositionV2 = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  salesPeriod,
  geographies,
  geoViewToggle = "aggregate",
  businessUnitToggle = "aggregate",
}: {
  startDate: string;
  endDate: string;
  brand: string;
  category: string;
  product: string;
  salesPeriod: SalesPeriod;
  geographies?: string[];
  geoViewToggle?: "aggregate" | "split";
  businessUnitToggle?: "aggregate" | "split";
}): Promise<SalesCompositionV2DTO> => {
  if (!startDate || !endDate) {
    return Promise.resolve(emptyV2Response);
  }
  return axiosApi.get("/api/v2/sales-drivers/sales-composition/v2", {
    baseURL: INSIGHTS_BACKEND_URL,
    params: {
      startDate: startDate,
      endDate: endDate,
      brand: brand,
      category: category,
      product: product,
      salesPeriod: salesPeriod,
      geographies: geographies,
      geoViewToggle: geoViewToggle,
      businessUnitToggle: businessUnitToggle,
    },
  });
};

type UseSalesCompositionV2Options = {
  config?: QueryConfig<typeof getSalesCompositionV2 | typeof getSalesCompositionNest>;
  request: {
    startDate: string;
    endDate: string;
    brand: string;
    category: string;
    product: string;
    salesPeriod: SalesPeriod;
    geographies?: string[];
    geoViewToggle?: "aggregate" | "split";
    businessUnitToggle?: "aggregate" | "split";
  };
};

export const useSalesCompositionV2 = ({ config, request }: UseSalesCompositionV2Options) => {
  const endpointType = useModuleEndpoint("sales-drivers");

  if (endpointType === "new") {
    return useQuery<SalesCompositionResponse, Error, SalesCompositionResponse>({
      ...config,
      queryKey: ["sales-composition-v2", getV2QueryKey(request), endpointType],
      queryFn: () => getSalesCompositionNest(request),
      select: (data) => data || emptyV2Response,
    });
  }

  return useQuery({
    queryKey: ["sales-composition-v2", getV2QueryKey(request), endpointType],
    queryFn: () => getSalesCompositionV2(request),
    select: (data) => data || emptyV2Response,
  });
};

const getV2QueryKey = (req: GetSalesCompositionV2Request): string => {
  return JSON.stringify(req);
};
